import { useState } from "react"
import { doc, updateDoc } from "firebase/firestore"
import { db } from "../../services/firebase"
import { parsearFecha, urgenciaViaje } from "../../utils/adminConstants"

export default function SeccionAgenda({ viajes, onCheckin }) {
  const [filtros, setFiltros] = useState({
    enViaje:    true,
    proximos:   true,
    pendientes: true,
    completados: false,
  })
  const [busqueda, setBusqueda] = useState("")
  const [verTodos, setVerTodos] = useState(false)
  const [editando, setEditando] = useState(null) // { viajeId, campo }
  const [valorEdit, setValorEdit] = useState("")

  async function guardarCampo(viaje, campo, valor) {
    try {
      const ref = doc(db, "usuarios_sheets", viaje.cliente_id, "viajes", viaje.id)
      await updateDoc(ref, { [campo]: valor })
      viaje[campo] = valor
    } catch(e) {
      console.error("Error guardando campo:", e)
    } finally {
      setEditando(null)
      setValorEdit("")
    }
  }

  function CeldaEditable({ viaje, campo, valor }) {
    const estaEditando = editando?.viajeId === viaje.id && editando?.campo === campo

    if (estaEditando) {
      return (
        <input
          autoFocus
          value={valorEdit}
          onChange={e => setValorEdit(e.target.value)}
          onBlur={() => guardarCampo(viaje, campo, valorEdit)}
          onKeyDown={e => {
            if (e.key === "Enter") guardarCampo(viaje, campo, valorEdit)
            if (e.key === "Escape") setEditando(null)
          }}
          style={{
            width: "100%", minWidth: "80px",
            background: "#1e3a5f", border: "1px solid #3b82f6",
            borderRadius: "6px", color: "#e5e7eb",
            fontFamily: "Inter, sans-serif", fontSize: "0.82rem",
            padding: "4px 8px", outline: "none"
          }}
        />
      )
    }

    return (
      <div
        onClick={() => { setEditando({ viajeId: viaje.id, campo }); setValorEdit(valor || "") }}
        title="Click para editar"
        style={{
          cursor: "pointer",
          minHeight: "24px",
          padding: "2px 6px",
          borderRadius: "6px",
          transition: "background 0.15s",
          color: valor ? "#e5e7eb" : "#374151",
          fontSize: "0.82rem",
        }}
        onMouseEnter={e => e.currentTarget.style.background = "#1a2332"}
        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
      >
        {valor || <span style={{ color:"#374151", fontSize:"0.75rem" }}>+ agregar</span>}
      </div>
    )
  }

  const hoy = new Date(); hoy.setHours(0,0,0,0)

  const fechaHoyFormateada = new Date().toLocaleDateString("es-AR", {
    weekday: "long", day: "numeric", month: "long", year: "numeric"
  })

  function toggleFiltro(key) {
    setFiltros(f => ({ ...f, [key]: !f[key] }))
  }

  const viajesFiltrados = viajes
    .filter(v => {
      const salida  = parsearFecha(v.fecha_salida)
      const regreso = parsearFecha(v.fecha_regreso)
      const estado  = (v.estado || "").toLowerCase()
      const diasSalida  = salida  ? Math.ceil((salida  - hoy) / 86400000) : null
      const diasRegreso = regreso ? Math.ceil((regreso - hoy) / 86400000) : null

      const esEnViaje    = diasSalida !== null && diasSalida < 0 && diasRegreso !== null && diasRegreso >= 0
      const esProximo    = diasSalida !== null && diasSalida >= 0 && diasSalida <= 30
      const esPendiente  = diasSalida !== null && diasSalida > 30
      const esCompletado = estado === "completado" || (diasRegreso !== null && diasRegreso < 0)

      if (esEnViaje    && !filtros.enViaje)     return false
      if (esProximo    && !filtros.proximos)    return false
      if (esPendiente  && !filtros.pendientes)  return false
      if (esCompletado && !filtros.completados) return false
      return true
    })
    .filter(v => {
      if (!busqueda) return true
      const nombre  = v.cliente_nombre || ""
      const destino = v.destino || ""
      return `${nombre} ${destino}`.toLowerCase().includes(busqueda.toLowerCase())
    })
    .sort((a, b) => {
      const fa = parsearFecha(a.fecha_salida)
      const fb = parsearFecha(b.fecha_salida)
      if (!fa && !fb) return 0
      if (!fa) return 1
      if (!fb) return -1
      return fa - fb
    })

  const viajesMostrados = verTodos ? viajesFiltrados : viajesFiltrados.slice(0, 50)

  function fmtCorta(str) {
    if (!str) return "—"
    const d = parsearFecha(str)
    if (!d) return "—"
    return d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit" })
  }

  function claseUrgencia(u) {
    if (u === "hoy")        return "urgencia-hoy"
    if (u === "regreso-hoy") return "urgencia-regreso"
    if (u === "proximo")    return "urgencia-proximo"
    if (u === "en-viaje")   return "urgencia-en-viaje"
    return ""
  }

  const BADGE_ESTADO = {
    "confirmado": "confirmado",
    "completado": "completado",
    "cancelado":  "cancelado",
    "en curso":   "en-curso",
    "en viaje":   "en-curso",
    "pagando":    "pagando",
    "prospecto":  "prospecto",
  }

  return (
    <div>
      <div className="admin__seccion-header">
        <div>
          <div className="admin__seccion-titulo">📅 Agenda de viajes</div>
          <div className="admin__seccion-sub">{fechaHoyFormateada}</div>
        </div>
      </div>

      {/* Filtros + buscador */}
      <div style={{ display:"flex", alignItems:"center", gap:"8px", flexWrap:"wrap", marginBottom:"16px" }}>
        {[
          { key:"enViaje",    label:"🌍 En viaje" },
          { key:"proximos",   label:"⏳ Próximos 30 días" },
          { key:"pendientes", label:"📋 Pendientes" },
          { key:"completados",label:"✅ Completados" },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => toggleFiltro(f.key)}
            className={"admin__filtro-btn" + (filtros[f.key] ? " active" : "")}
          >
            {f.label}
          </button>
        ))}
        <input
          className="admin__busqueda"
          placeholder="Buscar cliente o destino..."
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
          style={{ marginLeft:"auto", width:"220px" }}
        />
      </div>

      {/* Tabla */}
      <div className="admin__card" style={{ overflowX:"auto" }}>
        <table className="admin__tabla" style={{ width:"100%", borderCollapse:"collapse", fontSize:"0.82rem" }}>
          <thead>
            <tr style={{ borderBottom:"1px solid #2a2a2a" }}>
              {["#","Cliente","Destino","Salida","Regreso","Días","Noches","Aerolínea","Reserva","Vuelo ida","Vuelo regreso","Notas","Estado","Acción"].map(col => (
                <th key={col} style={{ padding:"8px 10px", textAlign:"left", color:"#6b7280", fontWeight:700, fontSize:"0.75rem", textTransform:"uppercase", letterSpacing:"0.4px", whiteSpace:"nowrap" }}>
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {viajesMostrados.length === 0 ? (
              <tr>
                <td colSpan={14} style={{ padding:"40px", textAlign:"center", color:"#4b5563" }}>
                  <div style={{ fontSize:"2rem", marginBottom:"8px" }}>📅</div>
                  No hay viajes para los filtros seleccionados.
                </td>
              </tr>
            ) : viajesMostrados.map((v, i) => {
              const urgencia = urgenciaViaje(v)
              const claseUr  = claseUrgencia(urgencia)
              const estadoKey = (v.estado || "").toLowerCase()
              const badgeClass = BADGE_ESTADO[estadoKey] || ""
              const notas = v.incluye ? (v.incluye.length > 40 ? v.incluye.slice(0, 40) + "…" : v.incluye) : null
              const salida = parsearFecha(v.fecha_salida)
              const diasHasta = salida ? Math.ceil((salida - hoy) / 86400000) : null

              return (
                <tr key={v.id || i} className={claseUr}>
                  <td style={{ padding:"8px 10px", color:"#6b7280", fontWeight:600 }}>{i + 1}</td>
                  <td style={{ padding:"8px 10px", whiteSpace:"nowrap" }}>
                    <div style={{ color:"#e5e7eb", fontWeight:700 }}>{v.cliente_nombre || "—"}</div>
                    {v.origen && <div style={{ fontSize:"0.75rem", color:"#6b7280", marginTop:"2px" }}>{v.origen}</div>}
                  </td>
                  <td style={{ padding:"8px 10px", color:"#e5e7eb" }}>{v.destino || "—"}</td>
                  <td style={{ padding:"8px 10px", whiteSpace:"nowrap" }}>
                    <div style={{ color:"#e5e7eb" }}>{fmtCorta(v.fecha_salida)}</div>
                    {v.hora_vuelo_salida && <div style={{ fontSize:"0.72rem", color:"#f59e0b", marginTop:"2px" }}>{v.hora_vuelo_salida}</div>}
                  </td>
                  <td style={{ padding:"8px 10px", whiteSpace:"nowrap" }}>
                    <div style={{ color:"#e5e7eb" }}>{fmtCorta(v.fecha_regreso)}</div>
                    {v.hora_vuelo_regreso && <div style={{ fontSize:"0.72rem", color:"#f59e0b", marginTop:"2px" }}>{v.hora_vuelo_regreso}</div>}
                  </td>
                  <td style={{ padding:"8px 10px", textAlign:"center", whiteSpace:"nowrap" }}>
                    {diasHasta === null ? <span style={{ color:"#4b5563" }}>—</span>
                      : diasHasta < 0    ? <span style={{ color:"#10b981", fontSize:"0.75rem" }}>En viaje</span>
                      : diasHasta === 0  ? <span style={{ color:"#f59e0b", fontWeight:700 }}>Hoy</span>
                      : diasHasta <= 3   ? <span style={{ color:"#ef4444", fontWeight:700 }}>{diasHasta}</span>
                      : diasHasta <= 7   ? <span style={{ color:"#f59e0b", fontWeight:700 }}>{diasHasta}</span>
                      : <span style={{ color:"#9ca3af" }}>{diasHasta}</span>}
                  </td>
                  <td style={{ padding:"8px 10px", textAlign:"center", color:"#e5e7eb" }}>{v.noches || "—"}</td>
                  <td style={{ padding:"8px 10px", color:"#e5e7eb" }}>{v.aerolinea || "—"}</td>
                  <td style={{ padding:"8px 10px", whiteSpace:"nowrap" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:"6px" }}>
                      <CeldaEditable viaje={v} campo="codigo_reserva" valor={v.codigo_reserva || v.reserva} />
                      {(v.codigo_reserva || v.reserva) && (
                        <button
                          onClick={() => navigator.clipboard.writeText(v.codigo_reserva || v.reserva)}
                          title="Copiar código"
                          style={{ background:"none", border:"none", cursor:"pointer", color:"#4b5563", fontSize:"0.85rem", padding:"2px", flexShrink:0 }}
                        >📋</button>
                      )}
                    </div>
                  </td>
                  <td style={{ padding:"8px 10px" }}>
                    <CeldaEditable viaje={v} campo="codigo_vuelo_salida" valor={v.codigo_vuelo_salida} />
                  </td>
                  <td style={{ padding:"8px 10px" }}>
                    <CeldaEditable viaje={v} campo="codigo_vuelo_regreso" valor={v.codigo_vuelo_regreso} />
                  </td>
                  <td style={{ padding:"8px 10px", maxWidth:"160px" }}>
                    {notas ? <span style={{ color:"#9ca3af", fontSize:"0.8rem" }}>{notas}</span> : <span style={{ color:"#4b5563" }}>—</span>}
                  </td>
                  <td style={{ padding:"8px 10px" }}>
                    <span className={"admin__badge " + badgeClass}>{v.estado || "—"}</span>
                  </td>
                  <td style={{ padding:"8px 10px", whiteSpace:"nowrap" }}>
                    {(v.estado === "En viaje" || urgencia === "en-viaje") ? (
                      <button onClick={() => onCheckin(v)} style={{ padding:"5px 12px", background:"#8b5cf6", border:"none", borderRadius:"6px", color:"white", fontFamily:"Inter, sans-serif", fontWeight:700, fontSize:"0.75rem", cursor:"pointer" }}>
                        Check-out
                      </button>
                    ) : urgencia === "hoy" ? (
                      <button onClick={() => onCheckin(v)} style={{ padding:"5px 12px", background:"#f59e0b", border:"none", borderRadius:"6px", color:"white", fontFamily:"Inter, sans-serif", fontWeight:700, fontSize:"0.75rem", cursor:"pointer" }}>
                        Check-in
                      </button>
                    ) : null}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div style={{ marginTop:"12px", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:"8px" }}>
        <div style={{ fontSize:"0.78rem", color:"#6b7280" }}>
          Mostrando {viajesMostrados.length} de {viajesFiltrados.length} viajes
        </div>
        {viajesFiltrados.length > 50 && !verTodos && (
          <button
            onClick={() => setVerTodos(true)}
            style={{ padding:"6px 16px", background:"#1f1f1f", border:"1px solid #3a3a3a", borderRadius:"8px", color:"#9ca3af", fontFamily:"Inter, sans-serif", fontWeight:700, fontSize:"0.8rem", cursor:"pointer" }}
          >
            Ver todos ({viajesFiltrados.length})
          </button>
        )}
      </div>

      {/* Leyenda */}
      <div style={{ marginTop:"16px", display:"flex", gap:"16px", flexWrap:"wrap", fontSize:"0.75rem", color:"#6b7280" }}>
        <span>🟡 Sale hoy/mañana</span>
        <span>🟢 En viaje</span>
        <span>🔵 Próximos 7 días</span>
        <span>🟣 Regresa hoy/mañana</span>
      </div>
    </div>
  )
}
